import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { FlowDefinition } from '@/flow-ir/types';

/**
 * The editable-canvas state (spec §5.3 — command-stack, transaction-grouped undo/redo). The Flow IR
 * `present` is authoritative; every structural edit is a whole-definition snapshot pushed onto the
 * undo stack (one user intention = one `commitEdit` = one undo step). Composite edits (edge splice)
 * compute the full next definition and commit once. Drag positions are EPHEMERAL canvas state, never
 * part of the IR (the schema has no position field — spec §4.6), so they do not enter the undo stack.
 */

export interface XY {
  x: number;
  y: number;
}

export interface DesignerState {
  /** The flow id currently open (route param). */
  flowId: string | null;
  /** Server flow id once created/loaded (may differ from a scratch id). */
  serverFlowId: string | null;
  /** The version loaded / last saved. */
  version: number | null;
  present: FlowDefinition | null;
  /** Last loaded-or-saved definition, for the dirty comparison + "reset to saved". */
  baseline: FlowDefinition | null;
  past: FlowDefinition[];
  future: FlowDefinition[];
  dirty: boolean;
  /** Ephemeral per-node drag positions (id → x/y). Cleared by Tidy. */
  positions: Record<string, XY>;
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  /** True once the flow is loaded into edit mode (vs the read-only sample path). */
  editable: boolean;
}

const UNDO_DEPTH = 500;

const initialState: DesignerState = {
  flowId: null,
  serverFlowId: null,
  version: null,
  present: null,
  baseline: null,
  past: [],
  future: [],
  dirty: false,
  positions: {},
  selectedNodeId: null,
  selectedEdgeId: null,
  editable: false,
};

const clone = (d: FlowDefinition): FlowDefinition => JSON.parse(JSON.stringify(d)) as FlowDefinition;
const defsEqual = (a: FlowDefinition | null, b: FlowDefinition | null): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

const designerSlice = createSlice({
  name: 'designer',
  initialState,
  reducers: {
    loadFlow(
      state,
      action: PayloadAction<{
        flowId: string;
        serverFlowId?: string;
        version?: number;
        def: FlowDefinition;
        editable: boolean;
      }>,
    ) {
      const { flowId, serverFlowId, version, def, editable } = action.payload;
      state.flowId = flowId;
      state.serverFlowId = serverFlowId ?? flowId;
      state.version = version ?? def.version ?? null;
      state.present = def;
      state.baseline = clone(def);
      state.past = [];
      state.future = [];
      state.dirty = false;
      state.positions = {};
      state.selectedNodeId = null;
      state.selectedEdgeId = null;
      state.editable = editable;
    },

    /** Commit a fully-computed next definition as one undo step. */
    commitEdit(state, action: PayloadAction<FlowDefinition>) {
      if (!state.present) return;
      state.past.push(state.present);
      if (state.past.length > UNDO_DEPTH) state.past.shift();
      state.present = action.payload;
      state.future = [];
      state.dirty = !defsEqual(state.present, state.baseline);
    },

    undo(state) {
      if (state.past.length === 0 || !state.present) return;
      const prev = state.past.pop()!;
      state.future.unshift(state.present);
      state.present = prev;
      state.dirty = !defsEqual(state.present, state.baseline);
    },

    redo(state) {
      if (state.future.length === 0 || !state.present) return;
      const next = state.future.shift()!;
      state.past.push(state.present);
      state.present = next;
      state.dirty = !defsEqual(state.present, state.baseline);
    },

    /** Ephemeral drag position — not an IR edit, not undoable. */
    setPosition(state, action: PayloadAction<{ id: string; pos: XY }>) {
      state.positions[action.payload.id] = action.payload.pos;
    },

    /** Drop one node's manual position (snap it back to the deterministic lane slot). */
    clearPosition(state, action: PayloadAction<string>) {
      delete state.positions[action.payload];
    },

    /** Tidy — drop manual positions, fall back to the deterministic lane layout. */
    resetPositions(state) {
      state.positions = {};
    },

    selectNode(state, action: PayloadAction<string | null>) {
      state.selectedNodeId = action.payload;
      state.selectedEdgeId = null;
    },

    selectEdge(state, action: PayloadAction<string | null>) {
      state.selectedEdgeId = action.payload;
      state.selectedNodeId = null;
    },

    /** After a successful save: the current present becomes the new saved baseline. */
    markSaved(state, action: PayloadAction<{ version: number; serverFlowId?: string }>) {
      if (!state.present) return;
      state.baseline = clone(state.present);
      state.version = action.payload.version;
      if (action.payload.serverFlowId) state.serverFlowId = action.payload.serverFlowId;
      state.dirty = false;
    },

    resetToSaved(state) {
      if (!state.baseline) return;
      state.past = [];
      state.future = [];
      state.present = clone(state.baseline);
      state.dirty = false;
    },

    clearDesigner() {
      return initialState;
    },
  },
});

export const {
  loadFlow,
  commitEdit,
  undo,
  redo,
  setPosition,
  clearPosition,
  resetPositions,
  selectNode,
  selectEdge,
  markSaved,
  resetToSaved,
  clearDesigner,
} = designerSlice.actions;

export const canUndo = (s: DesignerState) => s.past.length > 0;
export const canRedo = (s: DesignerState) => s.future.length > 0;

export default designerSlice.reducer;
